import React, {useRef, useState, useEffect, useCallback} from "react";

import {DepartureFlightIcon} from "../../assets/svgComponents/DepartureFlightIcon";
import {ReturnFlightIcon} from "../../assets/svgComponents/ReturnFlightIcon";
import {useTranslation} from "../../i18n/LanguageProvider";

interface Month {
    label: string; // z.B. "AUG"
    date: Date;
    index: number;
}

interface MonthTimelineProps {
    months: Month[];
    range: [number, number];
    setRange: (newRange: [number, number]) => void;
}

const clamp = (n: number, min: number, max: number) =>
    Math.max(min, Math.min(max, n));

const MonthTimeline: React.FC<MonthTimelineProps> = ({months, range, setRange}) => {
    const {t, locale} = useTranslation();
    const trackRef = useRef<HTMLDivElement>(null);
    const [dragging, setDragging] = useState<"start" | "end" | null>(null);

    const rangeRef = useRef(range);
    useEffect(() => {
        rangeRef.current = range;
    }, [range]);

    const getIndexFromPosition = useCallback((clientX: number): number => {
        if (!trackRef.current) return 0;
        const rect = trackRef.current.getBoundingClientRect();
        const relativeX = clientX - rect.left;
        const width = rect.width || 1;
        const N = Math.max(1, months.length);
        const idx = Math.round((relativeX / width) * (N - 1));
        return clamp(idx, 0, N - 1);
    }, [months.length]);

    const onPointerMove = useCallback((clientX: number) => {
        if (!dragging) return;
        const idx = getIndexFromPosition(clientX);
        const [start, end] = rangeRef.current;

        if (dragging === "start") {
            const newStart = Math.min(idx, end);
            if (newStart !== start) setRange([newStart, end]);
        } else {
            const newEnd = Math.max(idx, start);
            if (newEnd !== end) setRange([start, newEnd]);
        }
    }, [dragging, getIndexFromPosition, setRange]);

    const handleMouseMove = useCallback((e: MouseEvent) => {
        onPointerMove(e.clientX);
    }, [onPointerMove]);

    const handleTouchMove = useCallback((e: TouchEvent) => {
        if (e.touches && e.touches[0]) onPointerMove(e.touches[0].clientX);
    }, [onPointerMove]);

    const stopDragging = useCallback(() => setDragging(null), []);

    useEffect(() => {
        window.addEventListener("mousemove", handleMouseMove);
        window.addEventListener("mouseup", stopDragging);
        window.addEventListener("touchmove", handleTouchMove, {passive: false});
        window.addEventListener("touchend", stopDragging);
        return () => {
            window.removeEventListener("mousemove", handleMouseMove);
            window.removeEventListener("mouseup", stopDragging);
            window.removeEventListener("touchmove", handleTouchMove);
            window.removeEventListener("touchend", stopDragging);
        };
    }, [handleMouseMove, handleTouchMove, stopDragging]);

    const posPercent = (index: number): number => {
        const N = Math.max(1, months.length);
        if (N === 1) return 0;
        return (index / (N - 1)) * 100;
    };

    const leftPct = posPercent(range[0]);
    const rightPct = posPercent(range[1]);
    const widthPct = Math.max(0, rightPct - leftPct);

    return (
        <>
        <div className="month-select-row grid grid-cols-1 min-[380px]:grid-cols-2 gap-3 sm:hidden">
            {(["start", "end"] as const).map((edge, index) => (
                <label key={edge} className="min-w-0 text-xs font-bold text-primary">
                    <span className="month-label">{t(edge === "start" ? "calendar.fromMonth" : "calendar.toMonth")}</span>
                    <span className="month-select-control">
                    <span className="month-input-icon" aria-hidden="true">{edge === "start" ? <DepartureFlightIcon /> : <ReturnFlightIcon />}</span>
                    <select
                        className="mt-2 min-h-[44px] w-full rounded-xl border border-[#D8D8D8] bg-white px-3 text-base"
                        value={range[index]}
                        onChange={(event) => {
                            const value = Number(event.target.value);
                            setRange(edge === "start"
                                ? [value, Math.max(value, range[1])]
                                : [Math.min(value, range[0]), value]);
                        }}
                    >
                        {months.map((month, monthIndex) => (
                            <option key={monthIndex} value={monthIndex}>
                                {month.date.toLocaleDateString(locale, {month: "long", year: "numeric"})}
                            </option>
                        ))}
                    </select>
                    </span>
                </label>
            ))}
        </div>
        <div className="hidden sm:flex flex-col gap-3 w-full select-none">
            <div
                ref={trackRef}
                className="relative h-10 bg-[#F4F2FF] rounded-xl overflow-hidden"
            >
                {/* Month dividers*/}
                {months.map((_, i) => (
                    <div
                        key={i}
                        className="absolute top-1/2 -translate-y-1/2 h-6 w-[5px] rounded-full bg-[#B1A3FF] opacity-70 z-0"
                        style={{left: `${posPercent(i)}%`}}
                    />
                ))}

                {/* Active range bar*/}
                <div
                    className="absolute top-1/2 -translate-y-1/2 h-7 bg-[#7745F5] rounded-lg z-10"
                    style={{
                        left: `${leftPct}%`,
                        width: `calc(${widthPct}% + 1%)`,
                    }}
                />

                {/* Start handle */}
                <div
                    className="absolute top-1/2 -translate-y-1/2 w-6 h-7 rounded-full opacity-0 bg-white border-2 border-[#7745F5] cursor-pointer z-20"
                    style={{left: `${leftPct}%`}}
                    onMouseDown={() => setDragging("start")}
                    onTouchStart={(e) => {
                        e.preventDefault();
                        setDragging("start");
                    }}
                />

                {/* End handle */}
                <div
                    className="absolute top-1/2 -translate-y-1/2 w-6 h-7 rounded-full opacity-0 bg-white border-2 border-[#7745F5] cursor-pointer z-20"
                    style={{left: `${rightPct}%`}}
                    onMouseDown={() => setDragging("end")}
                    onTouchStart={(e) => {
                        e.preventDefault();
                        setDragging("end");
                    }}
                />
            </div>

            {/* Labels */}
            <div className="relative h-5 mt-1">
                {months.map((m, i) => {
                    const isActive = i >= range[0] && i <= range[1];
                    return (
                        <span
                            key={i}
                            className={`absolute -translate-x-1/2 text-xs ${
                                isActive ? "text-[#727272] font-semibold" : "text-[#E1E1E1]"
                            } whitespace-nowrap`}
                            style={{left: `${posPercent(i)}%`}}
                            title={`${m.label} ${m.date.getFullYear()}`}
                        >
              {m.label}
            </span>
                    );
                })}
            </div>
        </div>
        </>
    );
};

export default MonthTimeline;
